import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FolderSharedRoundedIcon from '@mui/icons-material/FolderSharedRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SourceRoundedIcon from '@mui/icons-material/SourceRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useOrgAccess } from '../../shared/contexts';
import { useAuth } from '../../shared/contexts/UserContext';
import {
  KNOWLEDGE_FILE_ACCEPT,
  deleteKnowledgeDocument,
  getKnowledgeOverview,
  listKnowledgeActivity,
  listKnowledgeDocuments,
  listKnowledgeJobs,
  retryKnowledgeJob,
  uploadDocumentToKnowledge,
  validateKnowledgeFile,
} from '../../shared/services/knowledgeLockerService';
import { consoleCardSx, microLabelSx, statusChipSx } from '../admin/consoleChips';
import {
  CAPABILITY_STATUSES,
  CapabilityStatusChip,
  CapabilityUnavailablePanel,
} from './capabilityStatus';
import {
  KNOWLEDGE_SOURCE_STATES,
  knowledgeSourcePresentation,
} from './knowledgeSourceStatus';

const TABS = ['sources', 'documents', 'access', 'activity'];
const ACTIVE_JOB_STATUSES = new Set(['uploading', 'queued', 'working', 'scanning', 'parsing', 'indexing', 'deletion_pending', 'deleting']);

const STATUS_LABELS = {
  uploading: 'Uploading',
  queued: 'Queued',
  working: 'Working',
  scanning: 'Scanning',
  parsing: 'Parsing',
  indexing: 'Indexing',
  ready: 'Ready',
  failed: 'Failed',
  deletion_pending: 'Deleting',
  deleting: 'Deleting',
  deleted: 'Deleted',
};

function humanize(value) {
  const text = String(value || '').replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Activity';
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 bytes';
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function scopeKey(scope) {
  if (scope?.type === 'firm') return 'firm';
  if (scope?.type === 'project' && scope.projectId) return `project:${scope.projectId}`;
  return '';
}

function scopeFromKey(value) {
  if (value === 'firm') return { type: 'firm' };
  if (String(value || '').startsWith('project:')) {
    return { type: 'project', projectId: String(value).slice('project:'.length) };
  }
  return null;
}

function scopeName(scope, scopes) {
  const targetKey = scopeKey(scope);
  return scopes.find((item) => scopeKey(item.type === 'firm'
    ? { type: 'firm' }
    : { type: 'project', projectId: item.id }) === targetKey)?.name || (scope?.type === 'firm' ? 'Firm library' : 'Project');
}

function statusTone(status) {
  if (status === 'ready') return 'success';
  if (status === 'failed') return 'error';
  if (ACTIVE_JOB_STATUSES.has(status)) return 'warning';
  return 'neutral';
}

export function useKnowledgeLocker(enabled = true) {
  const { currentUser } = useAuth();
  const [scopes, setScopes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityCursor, setActivityCursor] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activityError, setActivityError] = useState(null);

  const refresh = useCallback(async ({ silent = false, signal } = {}) => {
    if (!enabled || !currentUser) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [overview, documentPage, jobPage] = await Promise.all([
        getKnowledgeOverview(currentUser, { signal }),
        listKnowledgeDocuments(currentUser, { limit: 50, signal }),
        listKnowledgeJobs(currentUser, { limit: 50, signal }),
      ]);
      setScopes(Array.isArray(overview?.scopes) ? overview.scopes : []);
      setDocuments(Array.isArray(documentPage?.documents) ? documentPage.documents : []);
      setNextCursor(documentPage?.nextCursor || null);
      setJobs(Array.isArray(jobPage?.jobs) ? jobPage.jobs : []);
      setLoadedAt(Date.now());
    } catch (refreshError) {
      if (refreshError.name !== 'AbortError') setError(refreshError.message || 'Firm knowledge could not be loaded.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentUser, enabled]);

  useEffect(() => {
    if (!enabled || !currentUser) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    refresh({ signal: controller.signal });
    return () => controller.abort();
  }, [currentUser, enabled, refresh]);

  const activeJobSignature = useMemo(() => jobs
    .filter((job) => ACTIVE_JOB_STATUSES.has(job.status) || ACTIVE_JOB_STATUSES.has(job.stage))
    .map((job) => `${job.id}:${job.status}:${job.stage || ''}:${job.updatedAtIso || ''}`)
    .sort().join('|'), [jobs]);

  useEffect(() => {
    if (!enabled || !currentUser || !activeJobSignature) return undefined;
    let stopped = false;
    let timer = null;
    let delay = 2000;
    let lastSignature = activeJobSignature;
    const controller = new AbortController();

    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === 'hidden') {
          delay = Math.min(10000, delay * 2);
          schedule();
          return;
        }
        try {
          const [jobPage, documentPage] = await Promise.all([
            listKnowledgeJobs(currentUser, { limit: 50, signal: controller.signal }),
            listKnowledgeDocuments(currentUser, { limit: 50, signal: controller.signal }),
          ]);
          if (stopped) return;
          const nextJobs = Array.isArray(jobPage?.jobs) ? jobPage.jobs : [];
          const nextSignature = nextJobs
            .map((job) => `${job.id}:${job.status}:${job.stage || ''}:${job.updatedAtIso || ''}`)
            .sort().join('|');
          setJobs(nextJobs);
          setDocuments(Array.isArray(documentPage?.documents) ? documentPage.documents : []);
          setNextCursor(documentPage?.nextCursor || null);
          setLoadedAt(Date.now());
          const stillActive = nextJobs.some((job) => ACTIVE_JOB_STATUSES.has(job.status) || ACTIVE_JOB_STATUSES.has(job.stage));
          if (!stillActive) return;
          delay = nextSignature === lastSignature ? Math.min(10000, delay * 2) : 2000;
          lastSignature = nextSignature;
          schedule();
        } catch (pollError) {
          if (pollError.name !== 'AbortError' && !stopped) {
            delay = Math.min(10000, delay * 2);
            schedule();
          }
        }
      }, delay);
    };
    schedule();
    return () => {
      stopped = true;
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [activeJobSignature, currentUser, enabled]);

  const loadMoreDocuments = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listKnowledgeDocuments(currentUser, { limit: 50, cursor: nextCursor });
      const incoming = Array.isArray(page?.documents) ? page.documents : [];
      setDocuments((previous) => {
        const byId = new Map(previous.map((item) => [item.id, item]));
        incoming.forEach((item) => byId.set(item.id, item));
        return [...byId.values()];
      });
      setNextCursor(page?.nextCursor || null);
    } catch (loadError) {
      setError(loadError.message || 'More documents could not be loaded.');
    } finally {
      setLoadingMore(false);
    }
  }, [currentUser, loadingMore, nextCursor]);

  const upload = useCallback(async (file, scope, callbacks = {}) => {
    setBusyId('upload');
    setError(null);
    setNotice(null);
    try {
      const result = await uploadDocumentToKnowledge(currentUser, file, scope, callbacks);
      setNotice({ severity: 'success', text: `${file.name} is queued for secure processing.` });
      await refresh({ silent: true });
      return result;
    } catch (uploadError) {
      if (uploadError.name !== 'AbortError') setError(uploadError.message || 'The document could not be uploaded.');
      throw uploadError;
    } finally {
      setBusyId(null);
    }
  }, [currentUser, refresh]);

  const retry = useCallback(async (jobId) => {
    setBusyId(`job:${jobId}`);
    setError(null);
    try {
      await retryKnowledgeJob(currentUser, jobId);
      setNotice({ severity: 'success', text: 'Processing was queued again.' });
      await refresh({ silent: true });
    } catch (retryError) {
      setError(retryError.message || 'The job could not be retried.');
    } finally {
      setBusyId(null);
    }
  }, [currentUser, refresh]);

  const remove = useCallback(async (docId) => {
    setBusyId(`document:${docId}`);
    setError(null);
    try {
      await deleteKnowledgeDocument(currentUser, docId);
      setNotice({ severity: 'success', text: 'Secure deletion was queued.' });
      await refresh({ silent: true });
    } catch (deleteError) {
      setError(deleteError.message || 'The document could not be deleted.');
    } finally {
      setBusyId(null);
    }
  }, [currentUser, refresh]);

  const loadActivity = useCallback(async ({ reset = false } = {}) => {
    if (!currentUser || activityLoading) return;
    setActivityLoading(true);
    setActivityError(null);
    try {
      const page = await listKnowledgeActivity(currentUser, {
        limit: 50,
        cursorIso: reset ? null : activityCursor,
      });
      const incoming = (Array.isArray(page?.events) ? page.events : [])
        .filter((event) => event.type === 'locker_document' || event.type === 'locker_query');
      setActivity((previous) => reset ? incoming : [...previous, ...incoming]);
      setActivityCursor(page?.nextCursor || null);
      setActivityLoaded(true);
    } catch (activityLoadError) {
      setActivityError(activityLoadError.message || 'Knowledge activity could not be loaded.');
    } finally {
      setActivityLoading(false);
    }
  }, [activityCursor, activityLoading, currentUser]);

  return {
    scopes, documents, jobs, nextCursor, loading, loadingMore, busyId, error, notice, loadedAt,
    activity, activityCursor, activityLoading, activityLoaded, activityError,
    setError, setNotice, refresh, loadMoreDocuments, upload, retry, remove, loadActivity,
  };
}

function UploadDocumentsDialog({ open, scopes, busy, onClose, onUpload }) {
  const [file, setFile] = useState(null);
  const [destination, setDestination] = useState('');
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setDestination('');
    setProgress(0);
    setStage(null);
    setError(null);
  }, [open]);

  const close = () => {
    if (busy) abortRef.current?.abort();
    onClose();
  };

  const submit = async () => {
    const validationError = validateKnowledgeFile(file);
    if (validationError) { setError(validationError); return; }
    const scope = scopeFromKey(destination);
    if (!scope) { setError('Choose the firm library or a project.'); return; }
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    try {
      await onUpload(file, scope, {
        signal: controller.signal,
        onProgress: setProgress,
        onStage: setStage,
      });
      onClose();
    } catch (uploadError) {
      if (uploadError.name !== 'AbortError') setError(uploadError.message || 'Upload failed.');
    } finally {
      abortRef.current = null;
    }
  };

  const stageText = {
    hashing: 'Verifying the document',
    starting: 'Preparing a private upload',
    uploading: `Uploading ${progress}%`,
    queueing: 'Starting secure processing',
  }[stage];

  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>Upload firm knowledge</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
          Choose exactly where this document belongs. Restack will not make a project document available firm-wide.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography component="label" htmlFor="knowledge-destination" sx={{ ...microLabelSx, display: 'block', mb: 0.75 }}>
          Destination
        </Typography>
        <Select
          id="knowledge-destination"
          value={destination}
          onChange={(event) => setDestination(event.target.value)}
          displayEmpty
          fullWidth
          size="small"
          disabled={busy}
          inputProps={{ 'aria-label': 'Knowledge destination' }}
          sx={{ mb: 2 }}
        >
          <MenuItem value="" disabled>Choose a destination</MenuItem>
          {scopes.map((scope) => (
            <MenuItem key={scope.type === 'firm' ? 'firm' : scope.id} value={scope.type === 'firm' ? 'firm' : `project:${scope.id}`}>
              {scope.type === 'firm' ? 'Firm library' : `Project: ${scope.name}`}
            </MenuItem>
          ))}
        </Select>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={KNOWLEDGE_FILE_ACCEPT}
          onChange={(event) => {
            const selected = event.target.files?.[0] || null;
            setFile(selected);
            setError(selected ? validateKnowledgeFile(selected) : null);
          }}
          aria-label="Choose knowledge document"
        />
        <Paper
          elevation={0}
          onClick={() => !busy && inputRef.current?.click()}
          sx={(theme) => ({
            p: 2.5,
            borderRadius: '10px',
            border: `1px dashed ${theme.palette.divider}`,
            cursor: busy ? 'default' : 'pointer',
            backgroundColor: alpha(theme.palette.text.primary, 0.02),
          })}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CloudUploadRoundedIcon sx={{ color: 'text.secondary' }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {file?.name || 'Choose a document'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {file ? formatBytes(file.size) : 'PDF, PowerPoint, Word, Excel, CSV, Markdown, or text, up to 50 MB'}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" disabled={busy}>Browse</Button>
          </Stack>
        </Paper>
        {busy && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{stageText || 'Preparing upload'}</Typography>
              {stage === 'uploading' && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{progress}%</Typography>}
            </Stack>
            <LinearProgress variant={stage === 'uploading' ? 'determinate' : 'indeterminate'} value={progress} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close}>{busy ? 'Cancel upload' : 'Cancel'}</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={busy || !file || !destination}>
          {busy ? 'Uploading' : 'Upload and process'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Microsoft365SourceCard({ source = null, onAction = null }) {
  const theme = useTheme();
  const presentation = knowledgeSourcePresentation(source);
  const actionEnabled = presentation.actionAvailable && typeof onAction === 'function';

  return (
    <Paper elevation={0} sx={consoleCardSx(theme)}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '9px', display: 'grid', placeItems: 'center', backgroundColor: alpha(theme.palette.text.primary, 0.04) }}>
          <SourceRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Microsoft 365</Typography>
            <Chip size="small" variant="outlined" label={presentation.label} sx={statusChipSx(theme, presentation.tone)} />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}>
            SharePoint and OneDrive
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
            {presentation.explanation}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          disabled={!actionEnabled}
          onClick={actionEnabled ? onAction : undefined}
          aria-label={presentation.actionLabel}
        >
          {presentation.actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
}

function SourcesTab({ state, onOpenUpload, microsoftSource }) {
  const theme = useTheme();
  const activeCount = state.documents.filter((doc) => doc.status === 'ready').length;
  const processingCount = state.documents.filter((doc) => ACTIVE_JOB_STATUSES.has(doc.status)).length;
  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={consoleCardSx(theme)}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '9px', display: 'grid', placeItems: 'center', backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
            <CloudUploadRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Secure manual uploads</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              Add approved firm or project documents. Files are processed in Restack's private Google Cloud environment.
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
              {activeCount} ready{processingCount ? ` · ${processingCount} processing` : ''}
            </Typography>
          </Box>
          <Button variant="contained" disableElevation startIcon={<CloudUploadRoundedIcon />} onClick={onOpenUpload}>
            Upload document
          </Button>
        </Stack>
      </Paper>
      <Microsoft365SourceCard source={microsoftSource} />
    </Stack>
  );
}

function DocumentsTab({ state, onOpenUpload }) {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const normalizedQuery = query.trim().toLowerCase();
  const visible = state.documents.filter((doc) => {
    const queryMatch = !normalizedQuery || String(doc.name || '').toLowerCase().includes(normalizedQuery);
    const statusMatch = status === 'all' || doc.status === status;
    return doc.status !== 'deleted' && queryMatch && statusMatch;
  });
  const jobsByDocument = useMemo(() => {
    const map = new Map();
    state.jobs.forEach((job) => {
      if (job.docId && !map.has(job.docId)) map.set(job.docId, job);
    });
    return map;
  }, [state.jobs]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search documents"
          inputProps={{ 'aria-label': 'Search knowledge documents' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
          sx={{ flex: 1, maxWidth: 420 }}
        />
        <Select size="small" value={status} onChange={(event) => setStatus(event.target.value)} inputProps={{ 'aria-label': 'Filter document status' }} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="ready">Ready</MenuItem>
          <MenuItem value="queued">Queued</MenuItem>
          <MenuItem value="parsing">Parsing</MenuItem>
          <MenuItem value="indexing">Indexing</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </Select>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" disableElevation startIcon={<CloudUploadRoundedIcon />} onClick={onOpenUpload}>Upload</Button>
      </Stack>
      <TableContainer component={Paper} elevation={0} sx={consoleCardSx(theme, { padded: false })}>
        <Table size="small">
          <TableHead sx={{ '& th': microLabelSx }}>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((doc) => {
              const job = jobsByDocument.get(doc.id);
              const displayStatus = job?.status === 'working' && job?.stage ? job.stage : doc.status;
              return (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <DescriptionRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500, maxWidth: 360 }}>{doc.name || 'Untitled document'}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatBytes(doc.sizeBytes)}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2">{scopeName(doc.scope, state.scopes)}</Typography></TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={STATUS_LABELS[displayStatus] || humanize(displayStatus)} sx={statusChipSx(theme, statusTone(displayStatus))} />
                    {doc.status === 'failed' && doc.errorMessage && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5, maxWidth: 260 }}>{doc.errorMessage}</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatDate(doc.updatedAtIso)}</Typography></TableCell>
                  <TableCell align="right">
                    {job?.status === 'failed' && job.retryable && (
                      <Tooltip title="Retry processing">
                        <span><IconButton size="small" aria-label={`Retry ${doc.name}`} disabled={state.busyId === `job:${job.id}`} onClick={() => state.retry(job.id)}><ReplayRoundedIcon fontSize="small" /></IconButton></span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete securely">
                      <span><IconButton size="small" color="error" aria-label={`Delete ${doc.name}`} disabled={state.busyId === `document:${doc.id}` || ['deletion_pending', 'deleting'].includes(doc.status)} onClick={() => setDeleteTarget(doc)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {!visible.length && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>{state.documents.length ? 'No documents match these filters.' : 'No firm knowledge has been uploaded yet.'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {state.nextCursor && (
        <Box sx={{ textAlign: 'center', mt: 1.5 }}><Button variant="outlined" disabled={state.loadingMore} onClick={state.loadMoreDocuments}>{state.loadingMore ? 'Loading' : 'Load more'}</Button></Box>
      )}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>Delete this document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {deleteTarget?.name} will immediately leave search results. Restack will queue verified deletion of its source and index data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disableElevation onClick={async () => { const id = deleteTarget?.id; setDeleteTarget(null); if (id) await state.remove(id); }}>Delete securely</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AccessTab({ scopes }) {
  const theme = useTheme();
  return (
    <Box>
      <Alert severity="info" icon={<LockRoundedIcon />} sx={{ mb: 2 }}>
        Firm library documents are available to active firm members. Project documents are available only while working in that server-verified project context.
      </Alert>
      <Stack spacing={1.5}>
        {scopes.map((scope) => (
          <Paper key={scope.type === 'firm' ? 'firm' : scope.id} elevation={0} sx={consoleCardSx(theme)}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 38, height: 38, borderRadius: '9px', display: 'grid', placeItems: 'center', backgroundColor: alpha(theme.palette.text.primary, 0.04) }}>
                {scope.type === 'firm' ? <FolderSharedRoundedIcon sx={{ fontSize: 19, color: 'text.secondary' }} /> : <LockRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{scope.name || 'Project'}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                  {scope.type === 'firm'
                    ? 'Explicit firm-wide library. Uploads are never placed here automatically.'
                    : `${Array.isArray(scope.memberIds) ? scope.memberIds.length : 0} project member${Array.isArray(scope.memberIds) && scope.memberIds.length === 1 ? '' : 's'} · ${scope.projectCode || 'No project code'}`}
                </Typography>
              </Box>
              <Chip size="small" variant="outlined" label={scope.type === 'firm' ? 'Firm' : 'Project'} sx={statusChipSx(theme, 'neutral')} />
            </Stack>
          </Paper>
        ))}
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
        Project membership is managed from the organization and project records. Knowledge access cannot be widened from this screen.
      </Typography>
    </Box>
  );
}

function ActivityTab({ state }) {
  const theme = useTheme();
  useEffect(() => {
    if (!state.activityLoaded && !state.activityLoading) state.loadActivity({ reset: true });
  }, [state]);

  return (
    <Box>
      {state.activityError && <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => state.loadActivity({ reset: true })}>Retry</Button>}>{state.activityError}</Alert>}
      <TableContainer component={Paper} elevation={0} sx={consoleCardSx(theme, { padded: false })}>
        <Table size="small">
          <TableHead sx={{ '& th': microLabelSx }}>
            <TableRow><TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Action</TableCell><TableCell>Details</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {state.activity.map((event) => {
              const action = event.payload?.action || event.type;
              const details = [
                event.payload?.scopeType ? `Scope: ${event.payload.scopeType}` : null,
                event.payload?.sizeBytes ? `Size: ${formatBytes(event.payload.sizeBytes)}` : null,
                event.payload?.chunkCount != null ? `Chunks: ${event.payload.chunkCount}` : null,
                event.payload?.resultCount != null ? `Results: ${event.payload.resultCount}` : null,
              ].filter(Boolean).join(' · ');
              return (
                <TableRow key={event.id} hover>
                  <TableCell><Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatDate(event.atIso)}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{event.actorEmail || event.actorUid || 'System'}</Typography></TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={humanize(action)} sx={statusChipSx(theme, event.severity === 'ERROR' ? 'error' : 'neutral')} /></TableCell>
                  <TableCell><Typography variant="caption" sx={{ color: 'text.secondary' }}>{details || 'Metadata recorded'}</Typography></TableCell>
                </TableRow>
              );
            })}
            {!state.activity.length && !state.activityLoading && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>No Knowledge Locker activity has been recorded yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        {state.activityLoading && <LinearProgress />}
      </TableContainer>
      {state.activityCursor && (
        <Box sx={{ textAlign: 'center', mt: 1.5 }}><Button variant="outlined" disabled={state.activityLoading} onClick={() => state.loadActivity()}>Load more</Button></Box>
      )}
    </Box>
  );
}

export default function KnowledgeSection({
  sub,
  capability = null,
  microsoftSource = KNOWLEDGE_SOURCE_STATES.CLOUD_UNCONFIGURED,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const access = useOrgAccess();
  const resolvedCapability = capability || (access.isOrgAdmin ? {
      status: CAPABILITY_STATUSES.AVAILABLE,
      label: 'Available',
      explanation: 'Knowledge Locker is ready for firm and project documents.',
      actionEnabled: true,
    } : {
      status: CAPABILITY_STATUSES.ADMIN_MANAGED,
      label: 'Managed by your admin',
      explanation: 'Knowledge Locker is managed by your Organization Admin. Ask an admin about access or document sources.',
      actionEnabled: false,
    });
  const capabilityAvailable = resolvedCapability.status === CAPABILITY_STATUSES.AVAILABLE;
  const enabled = !access.isLoading && access.isOrgAdmin && capabilityAvailable;
  const state = useKnowledgeLocker(enabled);
  const [uploadOpen, setUploadOpen] = useState(false);
  const activeTab = TABS.includes(sub) ? sub : 'sources';

  useEffect(() => {
    if (enabled && !TABS.includes(sub)) navigate('/settings/knowledge/sources', { replace: true });
  }, [enabled, navigate, sub]);

  if (access.isLoading) {
    return <Box><Skeleton variant="rounded" height={48} sx={{ mb: 2 }} /><Skeleton variant="rounded" height={180} /></Box>;
  }
  if (!access.isOrgAdmin) {
    return (
      <CapabilityUnavailablePanel
        capability={resolvedCapability}
        title="Knowledge Locker"
        actionLabel="Manage documents"
      />
    );
  }
  if (!capabilityAvailable) {
    return (
      <CapabilityUnavailablePanel
        capability={resolvedCapability}
        title="Knowledge Locker"
        actionLabel="Upload documents"
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <CapabilityStatusChip capability={resolvedCapability} />
      </Stack>
      <Tabs
        value={activeTab}
        onChange={(_event, value) => navigate(`/settings/knowledge/${value}`)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Knowledge sections"
        sx={{ mb: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Tab value="sources" label="Sources" icon={<SourceRoundedIcon />} iconPosition="start" />
        <Tab value="documents" label="Documents" icon={<DescriptionRoundedIcon />} iconPosition="start" />
        <Tab value="access" label="Access" icon={<LockRoundedIcon />} iconPosition="start" />
        <Tab value="activity" label="Activity" icon={<HistoryRoundedIcon />} iconPosition="start" />
      </Tabs>

      {state.error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => state.setError(null)}>{state.error}</Alert>}
      {state.notice && <Alert severity={state.notice.severity} sx={{ mb: 2 }} onClose={() => state.setNotice(null)}>{state.notice.text}</Alert>}

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5} sx={{ mb: 1.5 }}>
        {state.loadedAt && <Typography variant="caption" sx={{ color: 'text.secondary' }}>Updated {formatDate(state.loadedAt)}</Typography>}
        <Tooltip title="Refresh">
          <span><IconButton size="small" aria-label="Refresh firm knowledge" disabled={state.loading} onClick={() => state.refresh()}><RefreshRoundedIcon sx={{ fontSize: 17 }} /></IconButton></span>
        </Tooltip>
      </Stack>

      {state.loading ? (
        <Stack spacing={1.5}>{[0, 1].map((item) => <Skeleton key={item} variant="rounded" height={112} />)}</Stack>
      ) : (
        <>
          {activeTab === 'sources' && (
            <SourcesTab
              state={state}
              onOpenUpload={() => setUploadOpen(true)}
              microsoftSource={microsoftSource}
            />
          )}
          {activeTab === 'documents' && <DocumentsTab state={state} onOpenUpload={() => setUploadOpen(true)} />}
          {activeTab === 'access' && <AccessTab scopes={state.scopes} />}
          {activeTab === 'activity' && <ActivityTab state={state} />}
        </>
      )}

      <UploadDocumentsDialog
        open={uploadOpen}
        scopes={state.scopes}
        busy={state.busyId === 'upload'}
        onClose={() => setUploadOpen(false)}
        onUpload={state.upload}
      />
      <Divider sx={{ mt: 3 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
        Knowledge retrieval is permission checked for every query. Document content is not exposed through the console activity feed.
      </Typography>
    </Box>
  );
}
